/**
 * 🔬 TECHNOLOGIES - EXPANDED TECH TREE
 * 
 * 70+ technologies organized across 11 tiers.
 * Each technology includes a quote from a real historical figure.
 * 
 * SCALING (2.5x cost per tier):
 * 
 * | Tier | Knowledge   | Secondary Resources | Research Time |
 * |------|-------------|---------------------|---------------|
 * |  1   | 15-25       | 25-75               | 15-30s        |
 * |  2   | 40-75       | 75-200              | 30s-1 min     |
 * |  3   | 100-175     | 150-400             | 1-2 min       |
 * |  4   | 250-450     | 400-1K              | 2-4 min       |
 * |  5   | 625-1.1K    | 1K-2.5K             | 4-8 min       |
 * |  6   | 1.5K-2.8K   | 2.5K-6K             | 8-12 min      |
 * |  7   | 4K-7K       | 6K-15K              | 12-20 min     |
 * |  8   | 10K-17K     | 15K-40K             | 20-30 min     |
 * |  9   | 25K-45K     | 40K-100K            | 30-60 min     |
 * | 10   | 60K-110K    | 100K-250K           | 1-2 hours     |
 * | 11   | 150K-300K   | 250K-600K           | 2-4 hours     |
 */

export const technologies = {
  // ═══════════════════════════════════════════════════════════════════════════
  // TIER 1: FOUNDATIONS (15-25 knowledge, 25-75 secondary)
  // ═══════════════════════════════════════════════════════════════════════════
  basic_tools: {
    id: 'basic_tools', name: 'Basic Tools', icon: '🔨', tier: 1,
    description: 'Craft simple tools for gathering',
    flavor: '"Man is a tool-using animal." — Thomas Carlyle',
    cost: { knowledge: 15, wood: 25, stone: 15 },
    requires: [], requiresBuildings: {},
    unlocks: ['mining', 'agriculture', 'construction'],
  },
  mining: {
    id: 'mining', name: 'Mining', icon: '⛏️', tier: 1,
    description: 'Extract stone from the earth',
    flavor: '"The nation that destroys its soil destroys itself." — Franklin D. Roosevelt',
    cost: { knowledge: 20, stone: 50 },
    requires: ['basic_tools'], requiresBuildings: {},
    unlocks: ['quarry'],
  },
  agriculture: {
    id: 'agriculture', name: 'Agriculture', icon: '🌾', tier: 1,
    description: 'Cultivate crops for food',
    flavor: '"Agriculture is the most healthful, most useful and most noble employment of man." — George Washington',
    cost: { knowledge: 20, food: 40, wood: 25 },
    requires: ['basic_tools'], requiresBuildings: {},
    unlocks: ['farm'],
  },
  construction: {
    id: 'construction', name: 'Construction', icon: '🏗️', tier: 1,
    description: 'Build basic structures',
    flavor: '"We shape our buildings; thereafter they shape us." — Winston Churchill',
    cost: { knowledge: 25, wood: 60, stone: 35 },
    requires: ['basic_tools'], requiresBuildings: {},
    unlocks: ['house'],
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // TIER 2: EARLY DEVELOPMENT (40-75 knowledge, 75-200 secondary)
  // ═══════════════════════════════════════════════════════════════════════════
  storage: {
    id: 'storage', name: 'Storage', icon: '📦', tier: 2,
    description: 'Build structures to store goods',
    flavor: '"A penny saved is a penny earned." — Benjamin Franklin',
    cost: { knowledge: 40, wood: 100, stone: 75 },
    requires: ['agriculture'], requiresBuildings: {},
    unlocks: ['granary'],
  },
  deep_mining: {
    id: 'deep_mining', name: 'Deep Mining', icon: '🏔️', tier: 2,
    description: 'Dig deeper for iron ore',
    flavor: '"If you want to find the secrets of the universe, think in terms of energy." — Nikola Tesla',
    cost: { knowledge: 60, stone: 150 },
    requires: ['mining'], requiresBuildings: { quarry: 2 },
    unlocks: ['deepMine'],
  },
  irrigation: {
    id: 'irrigation', name: 'Irrigation', icon: '💧', tier: 2,
    description: 'Water management for crops',
    flavor: '"Thousands have lived without love, not one without water." — W.H. Auden',
    cost: { knowledge: 50, wood: 75, stone: 100 },
    requires: ['agriculture'], requiresBuildings: { farm: 2 },
    unlocks: [],
  },
  animal_husbandry: {
    id: 'animal_husbandry', name: 'Animal Husbandry', icon: '🐄', tier: 2,
    description: 'Raise livestock for food',
    flavor: '"The greatness of a nation can be judged by the way its animals are treated." — Mahatma Gandhi',
    cost: { knowledge: 50, food: 125, wood: 75 },
    requires: ['agriculture'], requiresBuildings: {},
    unlocks: [],
  },
  writing: {
    id: 'writing', name: 'Writing', icon: '✍️', tier: 2,
    description: 'Record and share knowledge',
    flavor: '"The pen is mightier than the sword." — Edward Bulwer-Lytton',
    cost: { knowledge: 75, gold: 75 },
    requires: ['construction'], requiresBuildings: { house: 3 },
    unlocks: ['library'],
  },
  pottery: {
    id: 'pottery', name: 'Pottery', icon: '🏺', tier: 2,
    description: 'Craft vessels for storage',
    flavor: '"Shaping clay is like shaping ideas." — Bernard Leach',
    cost: { knowledge: 40, stone: 75, food: 50 },
    requires: ['agriculture'], requiresBuildings: {},
    unlocks: [],
  },
  woodworking: {
    id: 'woodworking', name: 'Woodworking', icon: '🪚', tier: 2,
    description: 'Advanced timber crafting',
    flavor: '"A bad workman blames his tools." — English Proverb',
    cost: { knowledge: 50, wood: 175 },
    requires: ['construction'], requiresBuildings: { woodcutter: 3 },
    unlocks: [],
  },
  necromancy: {
    id: 'necromancy', name: 'Necromancy', icon: '💀', tier: 2,
    description: 'Harness the power of death through ancient bone rituals',
    flavor: '"Death is not the greatest loss in life. The greatest loss is what dies inside us while we live." — Norman Cousins',
    cost: { knowledge: 50, bone: 25 },
    requires: ['writing'], requiresBuildings: {},
    unlocks: [],
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // TIER 3: ADVANCEMENT (100-175 knowledge, 150-400 secondary)
  // ═══════════════════════════════════════════════════════════════════════════
  metallurgy: {
    id: 'metallurgy', name: 'Metallurgy', icon: '⚙️', tier: 3,
    description: 'Smelt and work iron',
    flavor: '"Iron rusts from disuse; water loses its purity from stagnation." — Leonardo da Vinci',
    cost: { knowledge: 125, iron: 175, stone: 125 },
    requires: ['deep_mining'], requiresBuildings: { quarry: 3 },
    unlocks: ['mine'],
  },
  prospecting: {
    id: 'prospecting', name: 'Prospecting', icon: '🔍', tier: 3,
    description: 'Find precious metal deposits',
    flavor: '"Gold is a treasure, and he who possesses it does all he wishes." — Christopher Columbus',
    cost: { knowledge: 100, gold: 150, stone: 100 },
    requires: ['deep_mining'], requiresBuildings: {},
    unlocks: ['goldMine'],
  },
  masonry: {
    id: 'masonry', name: 'Masonry', icon: '🧱', tier: 3,
    description: 'Build with cut stone',
    flavor: '"We are all builders of temples." — Henry David Thoreau',
    cost: { knowledge: 110, stone: 250, iron: 75 },
    requires: ['mining', 'construction'], requiresBuildings: { quarry: 3 },
    unlocks: [],
  },
  literacy: {
    id: 'literacy', name: 'Literacy', icon: '📖', tier: 3,
    description: 'Widespread reading ability',
    flavor: '"A reader lives a thousand lives before he dies." — George R.R. Martin',
    cost: { knowledge: 140, gold: 125, culture: 40 },
    requires: ['writing'], requiresBuildings: { library: 1 },
    unlocks: ['scriptorium'],
  },
  medicine: {
    id: 'medicine', name: 'Medicine', icon: '💊', tier: 3,
    description: 'Heal the sick and wounded',
    flavor: '"Wherever the art of medicine is loved, there is also a love of humanity." — Hippocrates',
    cost: { knowledge: 125, food: 175, gold: 100 },
    requires: ['pottery', 'writing', 'necromancy'], requiresBuildings: {},
    unlocks: ['hospital'],
  },
  arts: {
    id: 'arts', name: 'Arts', icon: '🎨', tier: 3,
    description: 'Creative expression',
    flavor: '"Art is not what you see, but what you make others see." — Edgar Degas',
    cost: { knowledge: 100, gold: 125, culture: 60 },
    requires: ['writing'], requiresBuildings: {},
    unlocks: ['theater'],
  },
  crop_rotation: {
    id: 'crop_rotation', name: 'Crop Rotation', icon: '🔄', tier: 3,
    description: 'Sustainable farming methods',
    flavor: '"The earth is what we all have in common." — Wendell Berry',
    cost: { knowledge: 110, food: 200, wood: 125 },
    requires: ['irrigation', 'animal_husbandry'], requiresBuildings: { farm: 4 },
    unlocks: [],
  },
  religion: {
    id: 'religion', name: 'Religion', icon: '🙏', tier: 3,
    description: 'Organize spiritual beliefs',
    flavor: '"Science without religion is lame, religion without science is blind." — Albert Einstein',
    cost: { knowledge: 100, gold: 150, culture: 90 },
    requires: ['writing'], requiresBuildings: {},
    unlocks: ['temple'],
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // TIER 4: SPECIALIZATION (250-450 knowledge, 400-1000 secondary)
  // ═══════════════════════════════════════════════════════════════════════════
  blacksmithing: {
    id: 'blacksmithing', name: 'Blacksmithing', icon: '⚒️', tier: 4,
    description: 'Forge metal into tools and weapons using dungeon materials',
    flavor: '"Strike while the iron is hot." — English Proverb',
    cost: { knowledge: 275, iron: 450, coal: 100, bone: 15 },
    requires: ['metallurgy'], requiresBuildings: { mine: 1 },
    unlocks: ['forge'],
  },
  iron_working: {
    id: 'iron_working', name: 'Iron Working', icon: '🗡️', tier: 4,
    description: 'Forge iron tools and weapons reinforced with leather',
    flavor: '"The smith and his penny both are black." — George Herbert',
    cost: { knowledge: 320, iron: 550, coal: 150, leather: 20 },
    requires: ['blacksmithing'], requiresBuildings: { forge: 1 },
    unlocks: [],
  },
  smelting: {
    id: 'smelting', name: 'Smelting', icon: '🔥', tier: 4,
    description: 'Refine metal ores using bone fuel additives',
    flavor: '"Fire is the test of gold; adversity, of strong men." — Seneca',
    cost: { knowledge: 300, iron: 400, coal: 175, stone: 250, bone: 10 },
    requires: ['metallurgy'], requiresBuildings: { mine: 2 },
    unlocks: ['smelter'],
  },
  gemcutting: {
    id: 'gemcutting', name: 'Gem Cutting', icon: '💎', tier: 4,
    description: 'Polish gems using refined dungeon cloth',
    flavor: '"A diamond is a chunk of coal that did well under pressure." — Henry Kissinger',
    cost: { knowledge: 275, gold: 375, iron: 200, cloth: 15 },
    requires: ['prospecting'], requiresBuildings: { goldMine: 1 },
    unlocks: ['gemMine'],
  },
  currency: {
    id: 'currency', name: 'Currency', icon: '🪙', tier: 4,
    description: 'Standardized money backed by dungeon treasures',
    flavor: '"Money is a terrible master but an excellent servant." — P.T. Barnum',
    cost: { knowledge: 275, gold: 500, leather: 10 },
    requires: ['prospecting'], requiresBuildings: {},
    unlocks: ['market'],
  },
  education: {
    id: 'education', name: 'Education', icon: '📚', tier: 4,
    description: 'Study ancient texts preserved on dungeon cloth',
    flavor: '"Education is the most powerful weapon which you can use to change the world." — Nelson Mandela',
    cost: { knowledge: 360, gold: 450, culture: 175, cloth: 20 },
    requires: ['literacy'], requiresBuildings: { library: 2 },
    unlocks: ['academy'],
  },
  philosophy: {
    id: 'philosophy', name: 'Philosophy', icon: '🤔', tier: 4,
    description: 'Contemplate mortality using bone relics',
    flavor: '"The unexamined life is not worth living." — Socrates',
    cost: { knowledge: 320, culture: 275, gold: 225, bone: 25 },
    requires: ['literacy', 'arts'], requiresBuildings: {},
    unlocks: [],
  },
  military_training: {
    id: 'military_training', name: 'Military Training', icon: '⚔️', tier: 4,
    description: 'Train warriors using dungeon monster materials',
    flavor: '"The more you sweat in training, the less you bleed in combat." — Richard Marcinko',
    cost: { knowledge: 335, iron: 500, gold: 300, leather: 30, bone: 20 },
    requires: ['iron_working'], requiresBuildings: { forge: 1 },
    unlocks: ['barracks'],
  },
  archaeology: {
    id: 'archaeology', name: 'Archaeology', icon: '🏛️', tier: 4,
    description: 'Study ancient bones from dungeon expeditions',
    flavor: '"The past is never dead. It is not even past." — William Faulkner',
    cost: { knowledge: 300, gold: 350, culture: 225, bone: 35 },
    requires: ['arts', 'masonry'], requiresBuildings: {},
    unlocks: ['museum'],
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // TIER 5: INDUSTRY (625-1100 knowledge, 1000-2500 secondary)
  // ═══════════════════════════════════════════════════════════════════════════
  engineering: {
    id: 'engineering', name: 'Engineering', icon: '🔧', tier: 5,
    description: 'Design complex machines using dungeon-forged components',
    flavor: '"Scientists discover the world that exists; engineers create the world that never was." — Theodore von Kármán',
    cost: { knowledge: 750, iron: 1200, steel: 300, bone: 25, essence: 10 },
    requires: ['iron_working', 'smelting'], requiresBuildings: { forge: 2 },
    unlocks: ['workshop'],
  },
  steel_production: {
    id: 'steel_production', name: 'Steel Production', icon: '🔩', tier: 5,
    description: 'Create steel alloys infused with dungeon minerals',
    flavor: '"What is steel compared to the hand that wields it?" — Thulsa Doom',
    cost: { knowledge: 825, iron: 1500, coal: 600, darkSteel: 5, bone: 20 },
    requires: ['smelting'], requiresBuildings: { smelter: 1 },
    unlocks: ['steelMill'],
  },
  coal_mining: {
    id: 'coal_mining', name: 'Coal Mining', icon: '⚫', tier: 5,
    description: 'Extract coal for fuel using reinforced tools',
    flavor: '"Coal is a portable climate." — Ralph Waldo Emerson',
    cost: { knowledge: 625, iron: 850, stone: 725, leather: 30, cloth: 20 },
    requires: ['deep_mining', 'engineering'], requiresBuildings: { mine: 3 },
    unlocks: ['coalMine'],
  },
  higher_education: {
    id: 'higher_education', name: 'Higher Education', icon: '🎓', tier: 5,
    description: 'Advanced academic study of arcane and mundane',
    flavor: '"The roots of education are bitter, but the fruit is sweet." — Aristotle',
    cost: { knowledge: 950, gold: 1500, culture: 550, essence: 15, rune: 5 },
    requires: ['education', 'philosophy'], requiresBuildings: { academy: 1 },
    unlocks: ['university'],
  },
  commerce: {
    id: 'commerce', name: 'Commerce', icon: '📈', tier: 5,
    description: 'Advanced trading systems including dungeon goods',
    flavor: '"Trade is the natural enemy of all violent passions." — Montesquieu',
    cost: { knowledge: 700, gold: 1250, leather: 25, cloth: 15 },
    requires: ['currency', 'education'], requiresBuildings: { market: 2 },
    unlocks: ['tradingPost'],
  },
  architecture: {
    id: 'architecture', name: 'Architecture', icon: '🏰', tier: 5,
    description: 'Design grand structures reinforced with dungeon stone',
    flavor: '"Architecture is frozen music." — Johann Wolfgang von Goethe',
    cost: { knowledge: 875, stone: 1500, gold: 850, culture: 425, bone: 35, crystal: 8 },
    requires: ['masonry', 'education'], requiresBuildings: {},
    unlocks: ['mansion'],
  },
  tactics: {
    id: 'tactics', name: 'Military Tactics', icon: '🗺️', tier: 5,
    description: 'Strategic warfare learned from dungeon combat',
    flavor: '"The supreme art of war is to subdue the enemy without fighting." — Sun Tzu',
    cost: { knowledge: 800, steel: 500, iron: 1000, leather: 40, orcTusk: 10 },
    requires: ['military_training'], requiresBuildings: { barracks: 2 },
    unlocks: ['fortress'],
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // TIER 6: ADVANCED (1500-2800 knowledge, 2500-6000 secondary)
  // ═══════════════════════════════════════════════════════════════════════════
  industrialization: {
    id: 'industrialization', name: 'Industrialization', icon: '🏭', tier: 6,
    description: 'Mass production using dungeon-enhanced machinery',
    flavor: '"The factory of the future will have only two employees: a man and a dog." — Warren Bennis',
    cost: { knowledge: 1800, science: 275, steel: 1100, coal: 900, darkSteel: 15, essence: 20 },
    requires: ['engineering', 'coal_mining'], requiresBuildings: { workshop: 2 },
    unlocks: ['factory'],
  },
  power_generation: {
    id: 'power_generation', name: 'Power Generation', icon: '⚡', tier: 6,
    description: 'Harness energy using arcane crystals',
    flavor: '"Electricity is really just organized lightning." — George Carlin',
    cost: { knowledge: 2200, science: 450, steel: 1350, coal: 1100, crystal: 20, essence: 25 },
    requires: ['industrialization'], requiresBuildings: { factory: 1 },
    unlocks: ['powerPlant'],
  },
  trade_routes: {
    id: 'trade_routes', name: 'Trade Routes', icon: '🛤️', tier: 6,
    description: 'Establish distant trade in dungeon goods',
    flavor: '"He who controls the spice controls the universe." — Frank Herbert',
    cost: { knowledge: 1600, gold: 3250, leather: 50, orcTusk: 15 },
    requires: ['commerce'], requiresBuildings: { market: 3 },
    unlocks: ['tradingPost'],
  },
  sailing: {
    id: 'sailing', name: 'Sailing', icon: '⛵', tier: 6,
    description: 'Navigate the seas with reinforced hulls',
    flavor: '"Twenty years from now you will be more disappointed by the things you didn\'t do." — Mark Twain',
    cost: { knowledge: 1700, wood: 2700, gold: 1800, cloth: 40, darkSteel: 10 },
    requires: ['trade_routes', 'architecture'], requiresBuildings: {},
    unlocks: ['harbor'],
  },
  theology: {
    id: 'theology', name: 'Theology', icon: '⛪', tier: 6,
    description: 'Advanced spiritual study of dungeon mysteries',
    flavor: '"Faith is taking the first step even when you don\'t see the whole staircase." — Martin Luther King Jr.',
    cost: { knowledge: 1500, culture: 900, gold: 1250, rune: 15, ectoplasm: 10 },
    requires: ['religion', 'philosophy'], requiresBuildings: { temple: 1 },
    unlocks: [],
  },
  printing: {
    id: 'printing', name: 'Printing Press', icon: '📰', tier: 6,
    description: 'Mass produce written works on treated materials',
    flavor: '"The printing press is the greatest weapon in the armory of the modern commander." — T.E. Lawrence',
    cost: { knowledge: 2000, gold: 1800, culture: 900, cloth: 35, essence: 15 },
    requires: ['higher_education', 'engineering'], requiresBuildings: { university: 1 },
    unlocks: [],
  },
  advanced_metallurgy: {
    id: 'advanced_metallurgy', name: 'Advanced Metallurgy', icon: '⚗️', tier: 6,
    description: 'Create advanced alloys using dungeon minerals',
    flavor: '"Nature creates few things hard, many things soft." — Francis Bacon',
    cost: { knowledge: 1900, science: 360, steel: 1550, iron: 2200, darkSteel: 25, necroticEssence: 10 },
    requires: ['steel_production'], requiresBuildings: { steelMill: 2 },
    unlocks: [],
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // TIER 7: EMPIRE (4000-7000 knowledge, 6000-15000 secondary)
  // ═══════════════════════════════════════════════════════════════════════════
  banking: {
    id: 'banking', name: 'Banking', icon: '🏦', tier: 7,
    description: 'Financial institutions backed by dungeon treasures',
    flavor: '"Compound interest is the eighth wonder of the world." — Albert Einstein',
    cost: { knowledge: 4500, gold: 11250, science: 1125, crystal: 30, soulShard: 5 },
    requires: ['sailing', 'commerce'], requiresBuildings: { tradingPost: 2 },
    unlocks: ['bank'],
  },
  grand_architecture: {
    id: 'grand_architecture', name: 'Grand Architecture', icon: '🏛️', tier: 7,
    description: 'Monumental construction using rare materials',
    flavor: '"A great building must begin with the immeasurable." — Louis Kahn',
    cost: { knowledge: 5200, culture: 2575, stone: 8000, steel: 3200, dragonScale: 3, darkSteel: 40 },
    requires: ['theology', 'architecture'], requiresBuildings: { temple: 1 },
    unlocks: ['cathedral'],
  },
  monarchy: {
    id: 'monarchy', name: 'Monarchy', icon: '👑', tier: 7,
    description: 'Royal governance empowered by dungeon conquests',
    flavor: '"Uneasy lies the head that wears a crown." — William Shakespeare',
    cost: { knowledge: 4900, culture: 3200, gold: 9600, steel: 3850, demonHorn: 5, orcTusk: 25 },
    requires: ['grand_architecture', 'tactics'], requiresBuildings: { fortress: 1 },
    unlocks: ['palace'],
  },
  astronomy: {
    id: 'astronomy', name: 'Astronomy', icon: '🔭', tier: 7,
    description: 'Study celestial bodies through arcane lenses',
    flavor: '"Astronomy compels the soul to look upward." — Plato',
    cost: { knowledge: 4700, science: 1600, gold: 4500, gems: 800, crystal: 35, voidFragment: 3 },
    requires: ['higher_education', 'printing'], requiresBuildings: { university: 2 },
    unlocks: ['observatory'],
  },
  chemistry: {
    id: 'chemistry', name: 'Chemistry', icon: '🧪', tier: 7,
    description: 'Study of matter including dungeon reagents',
    flavor: '"Chemistry is the dirty part of physics." — Peter Reiss',
    cost: { knowledge: 4500, science: 1925, coal: 3200, steel: 1925, sulfur: 15, ectoplasm: 20 },
    requires: ['advanced_metallurgy', 'printing'], requiresBuildings: {},
    unlocks: [],
  },
  machinery: {
    id: 'machinery', name: 'Advanced Machinery', icon: '⚙️', tier: 7,
    description: 'Complex mechanical systems with rare components',
    flavor: '"The machine does not isolate man from the great problems of nature." — Antoine de Saint-Exupéry',
    cost: { knowledge: 5000, science: 1765, steel: 3850, coal: 2400, darkSteel: 50, necroticEssence: 15 },
    requires: ['power_generation', 'industrialization'], requiresBuildings: { factory: 3 },
    unlocks: [],
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // TIER 8: SCIENCE (10000-17000 knowledge, 15000-40000 secondary)
  // ═══════════════════════════════════════════════════════════════════════════
  scientific_method: {
    id: 'scientific_method', name: 'Scientific Method', icon: '🔬', tier: 8,
    description: 'Systematic experimentation with arcane specimens',
    flavor: '"The important thing is not to stop questioning." — Albert Einstein',
    cost: { knowledge: 12500, science: 6250, steel: 8750, gems: 2200, dragonScale: 8, voidFragment: 10 },
    requires: ['astronomy', 'chemistry'], requiresBuildings: { observatory: 1 },
    unlocks: ['laboratory'],
  },
  siege_warfare: {
    id: 'siege_warfare', name: 'Siege Warfare', icon: '🏰', tier: 8,
    description: 'Conquer fortifications with dungeon-forged weapons',
    flavor: '"In peace, sons bury their fathers. In war, fathers bury their sons." — Herodotus',
    cost: { knowledge: 11250, steel: 11250, iron: 13750, ogreHeart: 5, demonHorn: 10 },
    requires: ['monarchy', 'machinery'], requiresBuildings: { palace: 1 },
    unlocks: [],
  },
  arcane_arts: {
    id: 'arcane_arts', name: 'Arcane Arts', icon: '✨', tier: 8,
    description: 'Unlock magical studies through dungeon lore',
    flavor: '"Any sufficiently advanced technology is indistinguishable from magic." — Arthur C. Clarke',
    cost: { knowledge: 13750, culture: 7500, gems: 3750, gold: 11250, soulShard: 15, lichPhylactery: 3 },
    requires: ['astronomy', 'theology'], requiresBuildings: { cathedral: 1 },
    unlocks: ['wizardTower'],
  },
  physics: {
    id: 'physics', name: 'Physics', icon: '⚛️', tier: 8,
    description: 'Laws of the universe revealed through void study',
    flavor: '"God does not play dice with the universe." — Albert Einstein',
    cost: { knowledge: 13125, science: 7500, steel: 6250, voidFragment: 12, dragonHeart: 2 },
    requires: ['scientific_method'], requiresBuildings: { laboratory: 1 },
    unlocks: [],
  },
  economics: {
    id: 'economics', name: 'Economics', icon: '📊', tier: 8,
    description: 'Study of markets including rare dungeon goods',
    flavor: '"It is not from the benevolence of the butcher that we expect our dinner." — Adam Smith',
    cost: { knowledge: 11875, gold: 28125, science: 4375, soulShard: 10, vampireFang: 8 },
    requires: ['banking'], requiresBuildings: { bank: 2 },
    unlocks: [],
  },
  medicine_advanced: {
    id: 'medicine_advanced', name: 'Advanced Medicine', icon: '🏥', tier: 8,
    description: 'Modern medical practices using dungeon reagents',
    flavor: '"The art of medicine consists of amusing the patient while nature cures the disease." — Voltaire',
    cost: { knowledge: 12200, science: 5625, gold: 8750, bloodVial: 10, vampireFang: 5 },
    requires: ['chemistry', 'medicine'], requiresBuildings: { hospital: 1 },
    unlocks: [],
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // TIER 9: MASTERY (25000-45000 knowledge, 40000-100000 secondary)
  // ═══════════════════════════════════════════════════════════════════════════
  advanced_research: {
    id: 'advanced_research', name: 'Advanced Research', icon: '🧬', tier: 9,
    description: 'Cutting-edge science using epic dungeon components',
    flavor: '"Research is what I\'m doing when I don\'t know what I\'m doing." — Wernher von Braun',
    cost: { knowledge: 35000, science: 22500, steel: 22500, gems: 6250, dragonHeart: 5, divineEssence: 3 },
    requires: ['physics', 'scientific_method'], requiresBuildings: { laboratory: 2 },
    unlocks: ['researchCenter'],
  },
  castle_construction: {
    id: 'castle_construction', name: 'Castle Construction', icon: '🏯', tier: 9,
    description: 'Build mighty fortifications from rare materials',
    flavor: '"A man\'s home is his castle." — English Proverb',
    cost: { knowledge: 32500, steel: 43750, stone: 112500, gold: 43750, ancientBone: 20, thickHide: 30 },
    requires: ['siege_warfare', 'grand_architecture'], requiresBuildings: {},
    unlocks: ['grandCastle'],
  },
  alchemy: {
    id: 'alchemy', name: 'Alchemy', icon: '⚗️', tier: 9,
    description: 'Transform matter using epic reagents',
    flavor: '"Alchemy is not merely an art or science to teach metallic transmutation." — Paracelsus',
    cost: { knowledge: 33750, magic: 6250, gems: 8750, gold: 15000, lichPhylactery: 5, eldritchHeart: 2 },
    requires: ['arcane_arts', 'chemistry'], requiresBuildings: { wizardTower: 1 },
    unlocks: ['alchemyLab'],
  },
  enchanting: {
    id: 'enchanting', name: 'Enchanting', icon: '🔮', tier: 9,
    description: 'Imbue items with dungeon magic',
    flavor: '"Magic is believing in yourself." — Johann Wolfgang von Goethe',
    cost: { knowledge: 36250, magic: 9375, crystals: 3125, gems: 7500, voidEssence: 5, soulShard: 25 },
    requires: ['arcane_arts'], requiresBuildings: { wizardTower: 2 },
    unlocks: ['enchanter'],
  },
  crystal_synthesis: {
    id: 'crystal_synthesis', name: 'Crystal Synthesis', icon: '💎', tier: 9,
    description: 'Grow magical crystals from cosmic fragments',
    flavor: '"In a crystal we have clear evidence of a formative life principle." — Nikola Tesla',
    cost: { knowledge: 34375, magic: 8125, gems: 5625, gold: 22500, cosmicFragment: 3, voidFragment: 20 },
    requires: ['alchemy'], requiresBuildings: {},
    unlocks: [],
  },
  artifact_crafting: {
    id: 'artifact_crafting', name: 'Artifact Crafting', icon: '🏺', tier: 9,
    description: 'Create items of power from epic materials',
    flavor: '"We become what we behold. We shape our tools and then our tools shape us." — Marshall McLuhan',
    cost: { knowledge: 37500, magic: 12500, culture: 15000, crystals: 4375, starMetal: 5, dragonScale: 15 },
    requires: ['enchanting', 'archaeology'], requiresBuildings: { enchanter: 1 },
    unlocks: ['artifactForge'],
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // TIER 10: LEGENDARY (60000-110000 knowledge, 100000-250000 secondary)
  // ═══════════════════════════════════════════════════════════════════════════
  mysticism: {
    id: 'mysticism', name: 'Mysticism', icon: '🌀', tier: 10,
    description: 'Deep magical secrets from legendary components',
    flavor: '"The most beautiful thing we can experience is the mysterious." — Albert Einstein',
    cost: { knowledge: 75000, magic: 37500, science: 25000, crystals: 15000, voidEssence: 10, cosmicOre: 3 },
    requires: ['enchanting', 'alchemy'], requiresBuildings: { alchemyLab: 1, enchanter: 1 },
    unlocks: ['sanctum'],
  },
  legendary_construction: {
    id: 'legendary_construction', name: 'Legendary Construction', icon: '🌟', tier: 10,
    description: 'Build wonders using eternium alloys',
    flavor: '"Give me a lever long enough and I shall move the world." — Archimedes',
    cost: { knowledge: 82500, culture: 70000, science: 35000, steel: 87500, magic: 18750, eterniumIngot: 2, starMetal: 10 },
    requires: ['castle_construction', 'grand_architecture'], requiresBuildings: { grandCastle: 1 },
    unlocks: ['wonder'],
  },
  planar_theory: {
    id: 'planar_theory', name: 'Planar Theory', icon: '🌐', tier: 10,
    description: 'Study other dimensions through void fragments',
    flavor: '"There are more things in heaven and earth than are dreamt of in your philosophy." — William Shakespeare',
    cost: { knowledge: 78750, magic: 45000, science: 30000, crystals: 18750, cosmicFragment: 8, eldritchHeart: 5 },
    requires: ['mysticism', 'advanced_research'], requiresBuildings: { researchCenter: 1 },
    unlocks: [],
  },
  eternal_materials: {
    id: 'eternal_materials', name: 'Eternal Materials', icon: '💠', tier: 10,
    description: 'Create indestructible substances from cosmic ore',
    flavor: '"Nothing endures but change." — Heraclitus',
    cost: { knowledge: 76875, magic: 32500, artifacts: 6250, crystals: 12500, steel: 60000, cosmicOre: 5, starMetal: 15 },
    requires: ['crystal_synthesis', 'artifact_crafting'], requiresBuildings: { artifactForge: 1 },
    unlocks: [],
  },
  cosmic_awareness: {
    id: 'cosmic_awareness', name: 'Cosmic Awareness', icon: '🌌', tier: 10,
    description: 'Perceive the universe through legendary artifacts',
    flavor: '"Two things are infinite: the universe and human stupidity." — Albert Einstein',
    cost: { knowledge: 88500, magic: 55000, crystals: 17500, science: 30000, divineEssence: 10, eterniumIngot: 3 },
    requires: ['planar_theory', 'mysticism'], requiresBuildings: { sanctum: 1 },
    unlocks: [],
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // TIER 11: ULTIMATE (150000-300000 knowledge, 250000-600000 secondary)
  // ═══════════════════════════════════════════════════════════════════════════
  ley_lines: {
    id: 'ley_lines', name: 'Ley Lines', icon: '⚡', tier: 11,
    description: "Tap into earth's magical grid using legendary components",
    flavor: '"The earth has music for those who listen." — William Shakespeare',
    cost: { knowledge: 172500, magic: 93750, science: 62500, crystals: 31250, artifacts: 12500, cosmicOre: 10, eterniumIngot: 5 },
    requires: ['cosmic_awareness', 'eternal_materials'], requiresBuildings: { sanctum: 2 },
    unlocks: ['arcaneNexus'],
  },
  universal_knowledge: {
    id: 'universal_knowledge', name: 'Universal Knowledge', icon: '📚', tier: 11,
    description: 'Master all fields through cosmic understanding',
    flavor: '"The more I learn, the more I realize how much I don\'t know." — Albert Einstein',
    cost: { knowledge: 203125, science: 100000, magic: 68750, crystals: 25000, artifacts: 15625, divineEssence: 15, cosmicFragment: 12 },
    requires: ['advanced_research', 'cosmic_awareness'], requiresBuildings: { researchCenter: 2 },
    unlocks: ['eternalLibrary'],
  },
  dimensional_theory: {
    id: 'dimensional_theory', name: 'Dimensional Theory', icon: '🌀', tier: 11,
    description: 'Understand dimensional boundaries using void essence',
    flavor: '"Reality is merely an illusion, albeit a very persistent one." — Albert Einstein',
    cost: { knowledge: 250000, science: 125000, magic: 125000, crystals: 46875, artifacts: 25000, eterniumIngot: 8, voidEssence: 20 },
    requires: ['ley_lines', 'universal_knowledge'], requiresBuildings: { arcaneNexus: 1, eternalLibrary: 1 },
    unlocks: [],
  },
  starlight_harvesting: {
    id: 'starlight_harvesting', name: 'Starlight Harvesting', icon: '🌟', tier: 11,
    description: 'Capture celestial energy with star metal',
    flavor: '"We are all in the gutter, but some of us are looking at the stars." — Oscar Wilde',
    cost: { knowledge: 218750, magic: 140625, crystals: 56250, science: 75000, starMetal: 20, cosmicOre: 8 },
    requires: ['ley_lines'], requiresBuildings: { arcaneNexus: 1 },
    unlocks: [],
  },
  beacon_construction: {
    id: 'beacon_construction', name: 'Beacon Construction', icon: '🔮', tier: 11,
    description: 'Build the Dimensional Beacon from legendary materials',
    flavor: '"The journey of a thousand miles begins with a single step." — Lao Tzu',
    cost: { knowledge: 312500, science: 156250, magic: 156250, crystals: 78125, artifacts: 46875, eterniumIngot: 15, cosmicOre: 20, divineEssence: 25 },
    requires: ['dimensional_theory', 'starlight_harvesting'], requiresBuildings: {},
    unlocks: ['dimensionalBeacon'],
    isVictory: true,
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════

export const getTechsByTier = (tier) => 
  Object.values(technologies).filter(t => t.tier === tier);

export const getTechCount = () => 
  Object.keys(technologies).length;

export const getAvailableTechs = (researchedTechs, buildingCounts = {}) =>
  Object.values(technologies).filter(tech => {
    if (researchedTechs.includes(tech.id)) return false;
    if (!tech.requires.every(req => researchedTechs.includes(req))) return false;
    if (tech.requiresBuildings) {
      for (const [buildingId, count] of Object.entries(tech.requiresBuildings)) {
        if ((buildingCounts[buildingId] || 0) < count) return false;
      }
    }
    return true;
  });

export const getStarterTechs = () => 
  Object.values(technologies).filter(t => t.requires.length === 0);

export const canResearchTech = (techId, researchedTechs, buildingCounts = {}) => {
  const tech = technologies[techId];
  if (!tech) return false;
  if (researchedTechs.includes(techId)) return false;
  if (!tech.requires.every(req => researchedTechs.includes(req))) return false;
  if (tech.requiresBuildings) {
    for (const [buildingId, count] of Object.entries(tech.requiresBuildings)) {
      if ((buildingCounts[buildingId] || 0) < count) return false;
    }
  }
  return true;
};

export const getTechRequirements = (techId) => {
  const tech = technologies[techId];
  if (!tech) return { techs: [], buildings: {} };
  return { techs: tech.requires || [], buildings: tech.requiresBuildings || {} };
};

export default technologies;
